import { Button } from '@mui/material';
import { FMT } from '../../../util/formatter-service';
import { ColDef, ICellRendererParams, ValueFormatterParams } from 'ag-grid-community';
import { useSetAtom } from 'jotai';
import { useCallback, useMemo } from 'react';
import { AgTable } from '../../../components/AgTable/AgTable';
import { FadeInGridWrapper } from '../../../components/grid-renderers/FadeInGridWrapper';
import { DealDocDTO } from '../../../services/queries/DealDocQueries';
import { selectedActionState, selectedDocumentState } from './updateDealDocsState';

interface DealDocsGridProps {
  documents: DealDocDTO[];
}

export function DealDocsGrid({ documents }: DealDocsGridProps) {
  const setSelectedDocument = useSetAtom(selectedDocumentState);
  const setSelectedAction = useSetAtom(selectedActionState);

  const handleUpdateRound = useCallback(
    (doc: DealDocDTO) => {
      setSelectedDocument(doc);
      setSelectedAction('reassign');
    },
    [setSelectedDocument, setSelectedAction]
  );

  const columnDefs = useMemo<ColDef<DealDocDTO>[]>(
    () => [
      {
        field: 'fileName',
        headerName: 'File Name',
        minWidth: 200,
        flex: 2,
        sort: 'asc' as const,
        sortIndex: 0,
      },
      {
        field: 'documentType',
        headerName: 'Type',
        width: 120,
        valueFormatter: (params: ValueFormatterParams<DealDocDTO>) => {
          return params.value === 'captable' ? 'Cap Table' : 'Closing Doc';
        },
      },
      {
        field: 'fileSize',
        headerName: 'Size',
        width: 100,
      },
      {
        field: 'uploadedBy',
        headerName: 'Uploaded By',
        minWidth: 150,
        flex: 1.5,
      },
      {
        field: 'uploadSource',
        headerName: 'Source',
        width: 120,
        valueFormatter: (params: ValueFormatterParams<DealDocDTO>) => {
          return params.value === 'maggie' ? 'Maggie' : 'Starbreaker';
        },
      },
      {
        field: 'createdAt',
        headerName: 'Upload Date',
        width: 180,
        valueFormatter: (params: ValueFormatterParams<DealDocDTO>) => {
          return FMT.format('dateTime', params.value as string);
        },
      },
      {
        headerName: 'Actions',
        width: 150,
        sortable: false,
        cellRenderer: (params: ICellRendererParams<DealDocDTO>) => {
          if (!params.data) {
            return null;
          }
          return (
            <Button
              size='small'
              variant='outlined'
              color='primary'
              onClick={() => handleUpdateRound(params.data!)}
            >
              Update Round
            </Button>
          );
        },
      },
    ],
    [handleUpdateRound]
  );

  // Sort documents by fileName alphabetically (case-insensitive)
  const sortedDocuments = useMemo(
    () => [...documents].sort((a, b) => a.fileName.toLowerCase().localeCompare(b.fileName.toLowerCase())),
    [documents]
  );

  return (
    <FadeInGridWrapper>
      <AgTable rowData={sortedDocuments} columnDefs={columnDefs} domLayout='autoHeight' />
    </FadeInGridWrapper>
  );
}
